import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import Select, { components } from "react-select";
import DocCategoryService from "../../pages/DocCategory/DocCategoryService";
import { useTranslation } from "../../store/context/TranslationContext";

const compactStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9fb5d1" : "#d6deea",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.12)" : "none",
    background: "#ffffff",
    fontSize: 12.5,
    "&:hover": { borderColor: "#9fb5d1" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    gap: 2,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 32,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontSize: 12.5,
    marginLeft: 0,
    marginRight: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#1f2937",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#1f2937",
    margin: 0,
    maxWidth: "100%",
    fontSize: 12.5,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
    border: "1px solid #dbe4ef",
  }),
  menuList: (base) => ({ ...base, paddingTop: 4, paddingBottom: 4 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#f5f9ff" : state.isSelected ? "#eef5ff" : "#fff",
    color: "#1f2937",
    padding: "8px 10px",
    fontSize: 12.5,
  }),
  groupHeading: (base) => ({
    ...base,
    marginBottom: 2,
    padding: "6px 10px 2px",
    color: "#64748b",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "#475569" : "#94a3b8",
    padding: 4,
  }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
};

const Option = (props) => (
  <components.Option {...props}>
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1f2937" }}>{props.data.label}</span>
      {props.data.groupLabel ? (
        <span style={{ fontSize: 10.5, color: "#64748b" }}>{props.data.groupLabel}</span>
      ) : null}
    </div>
  </components.Option>
);

const SingleValue = (props) => (
  <components.SingleValue {...props}>
    <span style={{ fontSize: 12.5, color: "#1f2937" }}>{props.data.label}</span>
  </components.SingleValue>
);

function DocTypeSelectV2({ module = "", label, className = "", helperText, ...props }) {
  const [data, setData] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (DocCategoryService.originalData?.length) {
      setData(
        toJS(DocCategoryService.originalData)?.filter((record) => (module ? record.module === module : true))
      );
    } else {
      DocCategoryService.fetchSingleton();
    }
  }, [module, DocCategoryService?.originalData]);

  const groupedOptions = useMemo(
    () =>
      DocCategoryService.transformData(data).map((category) => ({
        label: t(category?.title),
        options: category?.children?.map((child) => ({
          value: child.value,
          label: t(child.title),
          groupValue: category.value,
          groupLabel: t(category.title),
        })),
      })),
    [data, t]
  );

  const flatOptions = useMemo(
    () => groupedOptions.flatMap((group) => group.options || []),
    [groupedOptions]
  );

  const selectedOption = useMemo(() => {
    const rawValue = props?.value?.value ?? props?.value;
    return flatOptions.find((option) => option.value === rawValue) || null;
  }, [flatOptions, props?.value]);

  return (
    <div className={className}>
      {label ? <label className="docv2__select-label">{t(label)}</label> : null}
      <Select
        inputId={props?.id}
        menuPortalTarget={typeof window !== "undefined" ? window.document.body : null}
        menuPlacement="auto"
        menuPosition="fixed"
        options={groupedOptions}
        value={selectedOption}
        onChange={props.onChange}
        placeholder={t(helperText || "Select document type")}
        isDisabled={props.isDisabled || false}
        isClearable
        isSearchable
        styles={compactStyles}
        classNamePrefix="docv2-select"
        components={{ Option, SingleValue }}
        noOptionsMessage={() => t("No document types found")}
      />
    </div>
  );
}

export default observer(DocTypeSelectV2);
